/**
 * Family registration persistence for pre-fill and completion status.
 * Saves form data so the next year the form can be pre-filled; marks completion
 * per school year so the gate stops redirecting once the user has submitted.
 * When the backend is implemented (Phase 3 Week 8), replace with API calls.
 */

/** School year runs Fall–Spring (e.g. July 2025 → "2025-2026"). */
export function getCurrentSchoolYear(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed; July = 6
  if (month >= 6) return `${year}-${year + 1}`;
  return `${year - 1}-${year}`;
}

const COMPLETE_PREFIX = 'bhg_family_registration_complete_';
const DATA_PREFIX = 'bhg_family_registration_data_';
const DRAFT_PREFIX = 'bhg_family_registration_draft_';

function getCompletionKey(userId: string): string {
  return `${COMPLETE_PREFIX}${userId}_${getCurrentSchoolYear()}`;
}

function getDataKey(userId: string): string {
  return `${DATA_PREFIX}${userId}`;
}

export function getFamilyRegistrationComplete(userId: string): boolean {
  try {
    return localStorage.getItem(getCompletionKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function setFamilyRegistrationComplete(userId: string): void {
  try {
    localStorage.setItem(getCompletionKey(userId), '1');
  } catch {
    // ignore
  }
}

/** Data we persist for next-year pre-fill (no signatures or policy checkboxes). */
export interface SavedFamilyRegistrationData {
  familyName: string;
  fatherName: string;
  motherName: string;
  phone: string;
  email: string;
  homeAddress: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  churchValue: string;
  churchOther: string;
  familyNotes: string;
  children: Array<{
    id: string;
    firstName: string;
    lastName: string;
    birthdate: string;
    grade: string;
  }>;
}

export function getSavedFamilyRegistrationData(
  userId: string
): SavedFamilyRegistrationData | null {
  try {
    const raw = localStorage.getItem(getDataKey(userId));
    if (!raw) return null;
    const data = JSON.parse(raw) as SavedFamilyRegistrationData;
    if (!data || typeof data !== 'object') return null;
    if (!Array.isArray(data.children)) return null;
    return data;
  } catch {
    return null;
  }
}

export function setSavedFamilyRegistrationData(
  userId: string,
  data: SavedFamilyRegistrationData
): void {
  try {
    localStorage.setItem(getDataKey(userId), JSON.stringify(data));
  } catch {
    // ignore
  }
}

/** In-progress registration (session) so policy links in a new tab do not lose the form. */
export interface FamilyRegistrationDraft extends SavedFamilyRegistrationData {
  step: number;
  medicalSignatureDataUrl: string | null;
  liabilitySignatureDataUrl: string | null;
  agreedPolicies: boolean;
  agreedDisciplinary: boolean;
  agreedDocIds: Record<string, boolean>;
}

function getDraftKey(userId: string): string {
  return `${DRAFT_PREFIX}${userId}_${getCurrentSchoolYear()}`;
}

export function getFamilyRegistrationDraft(userId: string): FamilyRegistrationDraft | null {
  try {
    const raw = sessionStorage.getItem(getDraftKey(userId));
    if (!raw) return null;
    const data = JSON.parse(raw) as FamilyRegistrationDraft;
    if (!data || typeof data !== 'object') return null;
    if (!Array.isArray(data.children)) return null;
    if (typeof data.step !== 'number' || data.step < 1 || data.step > 5) return null;
    return data;
  } catch {
    return null;
  }
}

export function setFamilyRegistrationDraft(userId: string, draft: FamilyRegistrationDraft): void {
  try {
    sessionStorage.setItem(getDraftKey(userId), JSON.stringify(draft));
  } catch {
    // ignore
  }
}

export function clearFamilyRegistrationDraft(userId: string): void {
  try {
    sessionStorage.removeItem(getDraftKey(userId));
  } catch {
    // ignore
  }
}
